import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';

import actionTypes from '~actionTypes';
import { servicesAndProductsTabValues } from '~constants';
import { SERVICE_CATEGORY_TYPE } from '~constants/order';
import type { Region } from '~types/districts/Region';
import type { Location } from '~types/locations/Location';
import type { Product } from '~types/products/Product';
import type { Promotion } from '~types/promotions/Promotion';
import type {
	ServiceCategoryWithServices,
	ServiceWithinCategory,
} from '~types/services/ServiceCategory';
import { createNamespacer, createReducer } from '~utils/reducers';

export interface ActivePromotionDetails
	extends Omit<Promotion, 'discountValue' | 'requirementValue'> {
	discountValue: number | string;
	requirementValue: number | string;
}

export interface PromotionsState {
	promotionsList: Promotion[];
	promotionsListCopy: Promotion[];
	promotionsListError: string;
	getAllPromotionsCallInProgress: boolean;
	activePromotionId: number | null;
	addNewPromotionCallInProgress: boolean;
	addNewPromotionError: string;
	showNewPromotionWizard: boolean;
	activePromotionDetails: ActivePromotionDetails | null;
	isPromotionDetailsLoading: boolean;
	promotionDetailsError: string;
	isInPromotionEditMode: boolean;
	searchText: string;
	roundedTabs: Array<{ label: string; value: string }>;
	activeRoundedTab: string;
	searchInProgress: boolean;
	promotionDetailsUpdateInProgress: boolean;
	promotionDetailsUpdateError: string;
	showNewPromotionsPricingScreen: boolean;
	newPromotionPriceItems: null;
	newPromotionName: string;
	numberOfActivePriceUpdates: number;
	stepInPromotionWizard: number;
	fetchLocationsCallInProgress: boolean;
	fetchLocationsListError: string;
	allLocations: {
		locations: Location[];
		needsRegions: boolean;
		regions: Region[];
		storesWithoutRegions: Location[];
	};
	selectedLocations: Location[];
	showProductsAndServicesScreen: boolean;
	servicesCallInProgress: boolean;
	servicesListCallError: string;
	servicesList: ServiceCategoryWithServices[];
	servicesListCopy: ServiceCategoryWithServices[];
	productsCallInProgress: boolean;
	productsListCallError: string;
	productsList: Product[];
	productsListCopy: Product[];
	selectAllServices: boolean;
	selectAllProducts: boolean;
	itemsCount: number;
	isLocationsLoading: boolean;
	locationsError: string;
	createPromotionCallInProgress: boolean;
	createPromotionError: string;
	isDetailsProductPickerVisible: boolean;
	newPromoAndValue: {
		name: string;
		discountValue: string;
	};
	hasDetailsEndDate: boolean;
	activePromotionDetailsUpdateErrors: Record<string, string | undefined>;
	referralProgramInfo: Record<string, string>;
	referralProgramInfoError: string;
	referralProgramInfoCallInProgress: boolean;
	referralActivityInfo: Record<string, string>;
	referralActivityInfoError: string;
	referralActivityInfoCallInProgress: boolean;
}

const initialState: PromotionsState = {
	promotionsList: [],
	promotionsListCopy: [],
	promotionsListError: '',
	getAllPromotionsCallInProgress: false,
	activePromotionId: null,
	addNewPromotionCallInProgress: false,
	addNewPromotionError: '',
	showNewPromotionWizard: false,
	activePromotionDetails: null,
	isPromotionDetailsLoading: false,
	promotionDetailsError: '',
	isInPromotionEditMode: false,
	searchText: '',
	roundedTabs: [
		{ label: 'Active', value: 'active' },
		{ label: 'Inactive', value: 'inactive' },
	],
	activeRoundedTab: 'active',
	searchInProgress: false,
	promotionDetailsUpdateInProgress: false,
	promotionDetailsUpdateError: '',
	showNewPromotionsPricingScreen: false,
	newPromotionPriceItems: null,
	newPromotionName: '',
	numberOfActivePriceUpdates: 0,
	stepInPromotionWizard: 1,
	fetchLocationsCallInProgress: false,
	fetchLocationsListError: '',
	allLocations: {
		regions: [],
		locations: [],
		needsRegions: false,
		storesWithoutRegions: [],
	},
	selectedLocations: [],
	showProductsAndServicesScreen: false,
	servicesCallInProgress: false,
	servicesListCallError: '',
	servicesList: [],
	servicesListCopy: [],
	productsCallInProgress: false,
	productsListCallError: '',
	productsList: [],
	productsListCopy: [],
	selectAllServices: false,
	selectAllProducts: false,
	itemsCount: 0,
	isLocationsLoading: false,
	// allLocations: null,
	locationsError: '',
	createPromotionCallInProgress: false,
	createPromotionError: '',
	isDetailsProductPickerVisible: false,
	newPromoAndValue: {
		name: '',
		discountValue: '',
	},
	hasDetailsEndDate: false,
	activePromotionDetailsUpdateErrors: {},
	referralProgramInfo: {},
	referralProgramInfoError: '',
	referralProgramInfoCallInProgress: false,
	referralActivityInfo: {},
	referralActivityInfoError: '',
	referralActivityInfoCallInProgress: false,
};

const nameSpace = 'BO-PROMOTIONS';
const promotionsNamespacer = createNamespacer(nameSpace);
const promotionsAT = actionTypes.businessOwner.globalSettings.promotions;

const handlers = {
	[promotionsNamespacer(promotionsAT.SET_ALL_PROMOTIONS)]: (state, action) => {
		const promotionsList: Promotion[] = sortBy(action.payload.promotionPrograms, [
			(promo) => promo.name.toLowerCase(),
		]);
		const activePromotions: Promotion[] = [];
		const inactivePromotions: Promotion[] = [];
		promotionsList.forEach((promotion) => {
			if (promotion.active) {
				activePromotions.push(promotion);
			} else {
				inactivePromotions.push(promotion);
			}
		});
		const activePromotionId =
			activePromotions.length !== 0
				? activePromotions[0].id
				: inactivePromotions.length !== 0
				? inactivePromotions[0].id
				: null;
		const activeRoundedTab =
			inactivePromotions.length !== 0 && activePromotions.length === 0
				? 'inactive'
				: 'active';

		return {
			...state,
			promotionsList,
			promotionsListCopy: promotionsList,
			activePromotionId,
			activeRoundedTab,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_PROMOTIONS_CALL_PROGRESS)]: (
		state,
		action,
	) => {
		return {
			...state,
			getAllPromotionsCallInProgress: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_PROMOTIONS_ERROR)]: (state, action) => {
		return {
			...state,
			promotionsListError: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_ACTIVE_PROMOTION)]: (state, action) => {
		return {
			...state,
			activePromotionId: action.payload,
			showNewPromotionWizard: false,
			isInPromotionEditMode: false,
			isDetailsProductPickerVisible: false,
			activePromotionDetailsUpdateErrors:
				initialState.activePromotionDetailsUpdateErrors,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_NEW_PROMOTION_CALL_PROGRESS)]: (
		state,
		action,
	) => {
		return {
			...state,
			addNewPromotionCallInProgress: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_NEW_PROMOTION_ERROR)]: (state, action) => {
		return {
			...state,
			addNewPromotionError: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_SHOW_NEW_PROMOTION_WIZARD)]: (
		state,
		action,
	) => {
		const additionalStateValue: Partial<PromotionsState> = {};
		let { newPromotionPriceItems, activeRoundedTab } = state;
		const { promotionsList } = state;
		const newPromoAndValue = { name: '', discountValue: '' };
		if (action.payload) {
			additionalStateValue.activePromotionId = null;
			activeRoundedTab = 'active';
		} else {
			if (state.activePromotionId === null && promotionsList.length > 0) {
				const activePromotions: Promotion[] = [];
				const inactivePromotions: Promotion[] = [];
				promotionsList.forEach((promotion) => {
					if (promotion.active) {
						activePromotions.push(promotion);
					} else {
						inactivePromotions.push(promotion);
					}
				});
				additionalStateValue.activePromotionId =
					activePromotions.length !== 0
						? activePromotions[0].id
						: inactivePromotions.length !== 0
						? inactivePromotions[0].id
						: null;
				activeRoundedTab =
					inactivePromotions.length !== 0 && activePromotions.length === 0
						? 'inactive'
						: 'active';
			}
			newPromotionPriceItems = null;
		}
		return {
			...state,
			showNewPromotionWizard: action.payload,
			isInPromotionEditMode: false,
			...additionalStateValue,
			addNewPromotionError: '',
			newPromotionPriceItems,
			activeRoundedTab,
			allSelected: false,
			newPromoAndValue,
			itemsCount: 0,
			createPromotionError: '',
		};
	},

	[promotionsNamespacer(promotionsAT.SET_PROMOTION_SEARCH_TEXT)]: (state, action) => {
		const { promotionsListCopy, activeRoundedTab } = state;
		const searchText = action.payload;
		let activePromotionId;

		const filteredPromotionsList = promotionsListCopy.length
			? promotionsListCopy.filter((promotion) => {
					const nameInLowerCase = promotion.name.toLowerCase();
					const searchTextInLowerCase = searchText.toLowerCase();
					return nameInLowerCase.indexOf(searchTextInLowerCase) > -1;
			  })
			: [];

		const activePromotions: Promotion[] = [];
		const inactivePromotions: Promotion[] = [];
		filteredPromotionsList.forEach((promotion) => {
			if (promotion.active) {
				activePromotions.push(promotion);
			} else {
				inactivePromotions.push(promotion);
			}
		});

		if (searchText === '') {
			if (state.searchInProgress) {
				activePromotionId = null;
			} else if (activeRoundedTab === 'active') {
				activePromotionId =
					activePromotions.length > 0 ? activePromotions[0].id : null;
			} else {
				activePromotionId =
					inactivePromotions.length > 0 ? inactivePromotions[0].id : null;
			}
		} else {
			activePromotionId =
				filteredPromotionsList.length > 0 ? filteredPromotionsList[0].id : null;
		}

		return {
			...state,
			promotionsList: filteredPromotionsList,
			searchText,
			activePromotionId,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_ACTIVE_ROUNDED_TAB)]: (state, action) => {
		let activePromotionId;
		const { promotionsList } = state;

		const activePromotions: Promotion[] = [];
		const inactivePromotions: Promotion[] = [];
		promotionsList.forEach((promotion) => {
			if (promotion.active) {
				activePromotions.push(promotion);
			} else {
				inactivePromotions.push(promotion);
			}
		});

		const additionalStateFields: Partial<PromotionsState> = {};

		if (!action.preventAutoSelect) {
			if (action.payload === 'active') {
				activePromotionId =
					activePromotions.length > 0 ? activePromotions[0].id : null;
			} else {
				activePromotionId =
					inactivePromotions.length > 0 ? inactivePromotions[0].id : null;
			}

			additionalStateFields.activePromotionId = activePromotionId;
		}

		return {
			...state,
			activeRoundedTab: action.payload,
			showNewPromotionWizard: false,
			...additionalStateFields,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_SEARCH_IN_PROGRESS)]: (state, action) => {
		return {
			...state,
			searchInProgress: action.payload,
			activePromotionId: initialState.activePromotionId,
			showNewPromotionWizard: false,
		};
	},

	[promotionsNamespacer(promotionsAT.GET_PROMOTION_DETAILS_STARTED)]: (state) => {
		return {
			...state,
			isPromotionDetailsLoading: true,
			activePromotionDetails: null,
			promotionDetailsError: '',
		};
	},

	[promotionsNamespacer(promotionsAT.GET_PROMOTION_DETAILS_SUCCEEDED)]: (
		state,
		action,
	) => {
		const { details } = action.payload;

		const hasDetailsEndDate = details.endDate ? true : false;
		return {
			...state,
			isPromotionDetailsLoading: false,
			activePromotionDetails: details,
			promotionDetailsError: '',
			hasDetailsEndDate,
		};
	},

	[promotionsNamespacer(promotionsAT.GET_PROMOTION_DETAILS_FAILED)]: (
		state,
		action,
	) => {
		return {
			...state,
			isPromotionDetailsLoading: false,
			activePromotionDetails: null,
			promotionDetailsError: action.paylaod,
		};
	},

	[promotionsNamespacer(promotionsAT.UPDATE_STEP_COUNT_IN_WIZARD)]: (state, action) => {
		return {
			...state,
			stepInPromotionWizard: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_LOCATIONS_CALL_PROGRESS)]: (state, action) => {
		return {
			...state,
			fetchLocationsCallInProgress: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_LOCATIONS_LIST_ERROR)]: (state, action) => {
		return {
			...state,
			fetchLocationsListError: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_ALL_LOCATIONS)]: (state, action) => {
		return {
			...state,
			allLocations: { ...action.payload },
		};
	},

	[promotionsNamespacer(promotionsAT.SET_SELECTED_LOCATION)]: (state, action) => {
		return {
			...state,
			selectedLocations: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SHOW_PRODUCTS_AND_SERVICES_SCREEN)]: (
		state,
		action,
	) => {
		return {
			...state,
			showProductsAndServicesScreen: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_SERVICES_CALL_PROGRESS)]: (state, action) => {
		return {
			...state,
			servicesCallInProgress: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_SERVICES_LIST_CALL_ERROR)]: (
		state,
		action,
	) => {
		return {
			...state,
			servicesListCallError: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_SERVICES_LIST)]: (
		state: PromotionsState,
		action,
	) => {
		const originalServicesList: ServiceCategoryWithServices[] = action.payload;
		let formattedServicesList: Array<Partial<ServiceCategoryWithServices>> = [];

		const perPoundServicesList: ServiceWithinCategory[] =
			originalServicesList.find((service) => service.category === 'PER_POUND')
				?.services || [];
		const fixedPriceServicesList: ServiceWithinCategory[] =
			originalServicesList.find((service) => service.category === 'FIXED_PRICE')
				?.services || [];

		let selectedItems: number[] = [];
		if (action.isDetails) {
			selectedItems =
				state.activePromotionDetails?.promotionItems
					?.filter((item) => item.promotionItemType === 'ServicesMaster')
					.map((item) => item.promotionItemId) ?? [];
		}
		const formattedPerPoundList = perPoundServicesList.map((perPoundService) => {
			let isSelected = false;
			if (action.isDetails) {
				isSelected = selectedItems.includes(perPoundService.id);
			}
			return {
				...perPoundService,
				isSelectedForPromotion: isSelected,
			};
		});
		const formattedFixedPriceList = fixedPriceServicesList.map(
			(fixedPriceService) => {
				let isSelected = false;
				if (action.isDetails) {
					isSelected = selectedItems.includes(fixedPriceService.id);
				}
				return {
					...fixedPriceService,
					isSelectedForPromotion: isSelected,
				};
			},
		);

		formattedServicesList = [
			{
				...originalServicesList.find(
					(service) => service.category === 'PER_POUND',
				),
				services: formattedPerPoundList,
			},
			{
				...originalServicesList.find(
					(service) => service.category === 'FIXED_PRICE',
				),
				services: formattedFixedPriceList,
			},
		];
		return {
			...state,
			servicesList: [...formattedServicesList],
			servicesListCopy: cloneDeep(formattedServicesList),
		};
	},

	[promotionsNamespacer(promotionsAT.SET_NEW_SERVICES_LIST)]: (state, action) => {
		const originalServicesList = action.payload;

		let selectedItems: number[] = [];
		if (action.isDetails) {
			selectedItems = state.activePromotionDetails?.promotionItems
				?.filter((item) => item.promotionItemType === 'ServicesMaster')
				.map((item) => item.promotionItemId);
		}

		let isSelected = false;
		originalServicesList.forEach((category) => {
			category.services.forEach((service) => {
				if (action.isDetails) {
					isSelected = selectedItems.includes(service.id);
				}
				service['isSelectedForPromotion'] = isSelected;
			});
		});

		return {
			...state,
			servicesList: [...originalServicesList],
			servicesListCopy: cloneDeep(originalServicesList),
		};
	},

	[promotionsNamespacer(promotionsAT.SET_PRODUCTS_CALL_PROGRESS)]: (state, action) => {
		return {
			...state,
			productsCallInProgress: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_PRODUCTS_LIST_CALL_ERROR)]: (
		state,
		action,
	) => {
		return {
			...state,
			productsListCallError: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_PRODUCTS_LIST)]: (state, action) => {
		let selectedItems: number[] = [];
		if (action.isDetails) {
			selectedItems = state.activePromotionDetails?.promotionItems
				?.filter((item) => item.promotionItemType === 'Inventory')
				.map((item) => item.promotionItemId);
		}

		const originalProductsList = action.payload;
		const formattedProductsList = originalProductsList.map((product) => {
			let isSelected = false;
			if (action.isDetails) {
				isSelected = selectedItems.includes(product.inventoryId);
			}
			return { ...product, isSelectedForPromotion: isSelected };
		});

		return {
			...state,
			productsList: formattedProductsList,
			productsListCopy: cloneDeep(formattedProductsList),
		};
	},

	[promotionsNamespacer(promotionsAT.UPDATE_SERVICES_LIST)]: (state, action) => {
		const { servicesList, activePromotionDetails, activePromotionId } = state;
		const { isSelectedForPromotion, categoryId, serviceId } = action.payload;

		// Immutably rebuild servicesList to avoid state mutations.
		const newServicesList = servicesList.map((category) => {
			if (category.id !== categoryId) {
				return category;
			}
			return {
				...category,
				services: category.services.map((service) =>
					service.id === serviceId
						? { ...service, isSelectedForPromotion }
						: service,
				),
			};
		});

		const categoryIndex = newServicesList.findIndex(
			(category) => categoryId === category.id,
		);
		const areAllServicesSelected = newServicesList[categoryIndex]?.services.every(
			(service) => service.isSelectedForPromotion,
		);

		let additionalStateFields = {};

		if (activePromotionId) {
			// This is being called from details and not from create new wizard
			const promotionItems = [...activePromotionDetails.promotionItems];

			const index = promotionItems
				.filter((promo) => promo.promotionItemType === 'ServicesMaster')
				.findIndex((promo) => promo.promotionItemId === serviceId);

			if (index > -1) {
				if (!isSelectedForPromotion) {
					promotionItems.splice(index, 1); // Remove from promotionItems
				}
			} else {
				if (isSelectedForPromotion) {
					promotionItems.push({
						promotionItemId: serviceId,
						promotionItemType: 'ServicesMaster',
					});
				}
			}

			additionalStateFields = {
				activePromotionDetails: {
					...activePromotionDetails,
					promotionItems: promotionItems,
				},
			};
		}

		return {
			...state,
			servicesList: newServicesList,
			selectAllServices: areAllServicesSelected,
			...additionalStateFields,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_SELECT_ALL_SERVICES)]: (state, action) => {
		const {
			servicesList,
			selectAllServices,
			activePromotionId,
			activePromotionDetails,
		} = state;

		// Flag ON: scope select-all to the active category's sub-categories (join by
		// sub-category id). Flag OFF: the legacy laundry/dry-cleaning string mapping.
		const { serviceCategory, subCategoryIds } = action.payload;
		const legacyCategoryType =
			serviceCategory === servicesAndProductsTabValues.LAUNDRY
				? SERVICE_CATEGORY_TYPE.LAUNDRY
				: SERVICE_CATEGORY_TYPE.DRY_CLEANING;

		// Immutably build new servicesList to avoid in-place mutation and ensure SET_ITEMS_COUNT can dispatch after.
		const newServicesList = servicesList.map((category) => {
			const doesCategoryMatch = subCategoryIds
				? subCategoryIds.includes(category.id)
				: category.categoryType === legacyCategoryType;

			if (!doesCategoryMatch) {
				return category;
			}

			return {
				...category,
				services: category.services.map((service) => ({
					...service,
					isSelectedForPromotion: !selectAllServices,
				})),
			};
		});

		let additionalItems = {};

		if (activePromotionId) {
			const promotionItems = activePromotionDetails.promotionItems.filter(
				(item) => item.promotionItemType !== 'ServicesMaster',
			);

			newServicesList.forEach((cat) => {
				cat.services.forEach((service) => {
					if (service.isSelectedForPromotion) {
						promotionItems.push({
							promotionItemId: service.id,
							promotionItemType: 'ServicesMaster',
						});
					}
				});
			});

			additionalItems = {
				activePromotionDetails: {
					...activePromotionDetails,
					promotionItems,
				},
			};
		}

		return {
			...state,
			selectAllServices: !selectAllServices,
			servicesList: newServicesList,
			...additionalItems,
		};
	},

	[promotionsNamespacer(promotionsAT.HANDLE_SERVICES_TAB_SWITCH)]: (state, action) => {
		/* 
		Whenever the Tab is switched between /Lb services and Fixed price services, need to run this case
		so that we re-caculate the selectAllServices state for that particular list of Services.
		 */
		const { servicesList } = state;

		// Flag ON: recompute select-all over the active category's services, matched
		// by sub-category id. Payload is { subCategoryIds }.
		const subCategoryIds = action.payload?.subCategoryIds;
		if (subCategoryIds) {
			const areAllActiveServicesSelected = servicesList
				.filter((category) => subCategoryIds.includes(category.id))
				.flatMap((category) => category.services)
				.every((service) => service.isSelectedForPromotion);

			return {
				...state,
				selectAllServices: areAllActiveServicesSelected,
			};
		}

		const serviceCategory =
			action.payload === 'lb_services' ? 'PER_POUND' : 'FIXED_PRICE';
		const category = servicesList.find(
			(category) => category.category === serviceCategory,
		);
		const areAllServicesSelected = category?.services.every(
			(service) => service.isSelectedForPromotion,
		);

		return {
			...state,
			selectAllServices: areAllServicesSelected,
		};
	},

	[promotionsNamespacer(promotionsAT.UPDATE_PRODUCTS_LIST)]: (state, action) => {
		const { isSelectedForPromotion, inventoryId } = action.payload;
		const { productsList, activePromotionId, activePromotionDetails } = state;

		// Build a new productsList immutably (no in-place mutation) so the
		// immutable-state-invariant middleware does not abort the dispatch chain
		// before the follow-up SET_ITEMS_COUNT dispatch.
		const newProductsList = productsList.map((product) =>
			product.inventoryId === inventoryId
				? { ...product, isSelectedForPromotion }
				: product,
		);

		const areAllProductsSelected = newProductsList.every(
			(product) => product.isSelectedForPromotion,
		);

		let additionalStateFields = {};

		if (activePromotionId) {
			// This is being called from details and not from create new wizard
			const promotionItems = [...activePromotionDetails.promotionItems];

			const index = promotionItems
				.filter((promo) => promo.promotionItemType === 'Inventory')
				.findIndex((promo) => promo.promotionItemId === inventoryId);

			if (index > -1) {
				if (!isSelectedForPromotion) {
					promotionItems.splice(index, 1); // Remove from promotionItems
				}
			} else {
				if (isSelectedForPromotion) {
					promotionItems.push({
						promotionItemId: inventoryId,
						promotionItemType: 'Inventory',
					});
				}
			}

			additionalStateFields = {
				activePromotionDetails: {
					...activePromotionDetails,
					promotionItems: promotionItems,
				},
			};
		}

		return {
			...state,
			productsList: newProductsList,
			selectAllProducts: areAllProductsSelected,
			...additionalStateFields,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_SELECT_ALL_PRODUCTS)]: (state) => {
		const {
			productsList,
			selectAllProducts,
			activePromotionDetails,
			activePromotionId,
		} = state;
		const updatedProductsList = productsList.map((product) => ({
			...product,
			isSelectedForPromotion: !selectAllProducts,
		}));
		let additionalStateFields = {};

		if (activePromotionId) {
			const promotionItems = activePromotionDetails.promotionItems.filter(
				(item) => item.promotionItemType !== 'Inventory',
			);
			updatedProductsList.forEach((product) => {
				if (product.isSelectedForPromotion) {
					promotionItems.push({
						promotionItemId: product.inventoryId,
						promotionItemType: 'Inventory',
					});
				}
			});

			additionalStateFields = {
				activePromotionDetails: {
					...activePromotionDetails,
					promotionItems,
				},
			};
		}
		return {
			...state,
			selectAllProducts: !selectAllProducts,
			productsList: updatedProductsList,
			...additionalStateFields,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_ITEMS_COUNT)]: (state) => {
		const { servicesList, productsList } = state;
		let itemsCount = 0;

		// We can assume cents2.0 serviceList will have more than 2 objects in the list
		// Because we specifically map <cents2.0 to only have 2 objects in the list
		// We also check to make sure ONLY PER_POUND and FIXED_PRICE categories exist for <cents2.0
		const isNotCents20CheckArr = servicesList.map((category) =>
			['PER_POUND', 'FIXED_PRICE'].includes(category.category),
		);
		const isNotCents20 =
			servicesList.length === 2 && isNotCents20CheckArr.every((val) => val);

		if (isNotCents20) {
			const perPoundItemsCount =
				servicesList
					.find((category) => category.category === 'PER_POUND')
					?.services.reduce((acc, service) => {
						if (service.isSelectedForPromotion) {
							acc = acc + 1;
						}
						return acc;
					}, 0) || 0;

			const fixedPriceItemsCount =
				servicesList
					.find((category) => category.category === 'FIXED_PRICE')
					?.services.reduce((acc, service) => {
						if (service.isSelectedForPromotion) {
							acc = acc + 1;
						}
						return acc;
					}, 0) || 0;

			const productsCount =
				productsList?.reduce((acc, product) => {
					if (product.isSelectedForPromotion) {
						acc = acc + 1;
					}
					return acc;
				}, 0) || 0;

			itemsCount = perPoundItemsCount + fixedPriceItemsCount + productsCount;
		} else {
			let promotionServicesCount = 0;
			servicesList.forEach((category) => {
				if (category?.services) {
					const count = category?.services?.reduce((acc, service) => {
						if (service.isSelectedForPromotion) {
							acc++;
						}
						return acc;
					}, 0);
					promotionServicesCount += count;
				}
			});

			const productsCount = productsList?.reduce((acc, product) => {
				if (product.isSelectedForPromotion) {
					acc++;
				}
				return acc;
			}, 0);

			itemsCount = promotionServicesCount + productsCount;
		}
		return {
			...state,
			itemsCount,
		};
	},

	[promotionsNamespacer(promotionsAT.RESET_SERVICES_AND_PRODUCTS)]: (state) => {
		const { servicesListCopy, productsListCopy } = state;
		return {
			...state,
			servicesList: cloneDeep(servicesListCopy),
			productsList: cloneDeep(productsListCopy),
		};
	},

	[promotionsNamespacer(promotionsAT.HANDLE_PRODUCTS_TAB_SWITCH)]: (state) => {
		const { productsList } = state;
		const areAllProductsSelected = productsList.every(
			(product) => product.isSelectedForPromotion,
		);
		return {
			...state,
			selectAllProducts: areAllProductsSelected,
		};
	},

	[promotionsNamespacer(promotionsAT.UPDATE_SERVICES_AND_PRODUCTS_COPY)]: (state) => {
		const { servicesList, productsList } = state;
		return {
			...state,
			servicesListCopy: cloneDeep(servicesList),
			productsListCopy: cloneDeep(productsList),
		};
	},

	[promotionsNamespacer(promotionsAT.RESET_WIZARD_REDUX_STATE)]: (state) => {
		return {
			...state,
			stepInPromotionWizard: 1,
			selectedLocations: [],
			showProductsAndServicesScreen: false,
			servicesCallInProgress: false,
			servicesListCallError: '',
			servicesList: [],
			servicesListCopy: [],
			productsCallInProgress: false,
			productsListCallError: '',
			productsList: [],
			productsListCopy: [],
			selectAllServices: false,
			selectAllProducts: false,
			itemsCount: 0,
		};
	},

	[promotionsNamespacer(promotionsAT.GET_ALL_LOCATIONS_STARTED)]: (state) => {
		return {
			...state,
			isLocationsLoading: true,
			allLocations: null,
			locationsError: '',
		};
	},

	[promotionsNamespacer(promotionsAT.GET_ALL_LOCATIONS_SUCCEEDED)]: (state, action) => {
		return {
			...state,
			isLocationsLoading: false,
			allLocations: action.payload,
			locationsError: '',
		};
	},

	[promotionsNamespacer(promotionsAT.GET_ALL_LOCATIONS_FAILED)]: (state, action) => {
		return {
			...state,
			isLocationsLoading: false,
			allLocations: null,
			locationsError: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_CREATE_PROMOTION_CALL_PROGRESS)]: (
		state,
		action,
	) => {
		return {
			...state,
			createPromotionCallInProgress: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_CREATE_PROMOTION_CALL_ERROR)]: (
		state,
		action,
	) => {
		return {
			...state,
			createPromotionError: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_DETAILS_PRODUCT_PICKER_VISIBILITY)]: (
		state,
		action,
	) => {
		return {
			...state,
			isDetailsProductPickerVisible: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.CLEAR_SERVICES_AND_PRODUCTS_DATA)]: (state) => {
		return {
			...state,
			servicesCallInProgress: false,
			servicesListCallError: '',
			servicesList: [],
			servicesListCopy: [],
			productsCallInProgress: false,
			productsListCallError: '',
			productsList: [],
			productsListCopy: [],
			selectAllServices: false,
			selectAllProducts: false,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_NEW_PROMO_NAME_AND_DISCOUNT_VALUE)]: (
		state,
		action,
	) => {
		return {
			...state,
			newPromoAndValue: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.ACTIVE_PROMO_DETAILS_CHANGED)]: (
		state,
		action,
	) => {
		const updatedActivePromoDetails = {
			...state.activePromotionDetails,
			...action.payload,
		};
		return {
			...state,
			activePromotionDetails: updatedActivePromoDetails,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_DETAILS_HAS_END_DATE)]: (state, action) => {
		return {
			...state,
			hasDetailsEndDate: action.payload,
		};
	},

	[promotionsNamespacer(promotionsAT.UPDATE_PROMO_DETAIL_IN_LIST)]: (state, action) => {
		const { id } = action.payload;
		delete action.payload.id;
		const promotionsList = cloneDeep(state.promotionsList);
		const promotionsListCopy = cloneDeep(state.promotionsListCopy);
		const currentPromoIndexForPromotionstList = promotionsList.findIndex(
			(promo) => promo.id === id,
		);
		const currentPromoIndexForPromotionstListCopy = promotionsListCopy.findIndex(
			(promo) => promo.id === id,
		);
		if (currentPromoIndexForPromotionstList !== -1) {
			promotionsList[currentPromoIndexForPromotionstList] = {
				...promotionsList[currentPromoIndexForPromotionstList],
				...action.payload,
			};
			promotionsListCopy[currentPromoIndexForPromotionstListCopy] = {
				...promotionsListCopy[currentPromoIndexForPromotionstListCopy],
				...action.payload,
			};
		}
		const sortedPromotionsList = sortBy(promotionsList, [
			(promo) => promo.name.toLowerCase(),
		]);
		const sortedPromotionsListCopy = sortBy(promotionsListCopy, [
			(promo) => promo.name.toLowerCase(),
		]);
		return {
			...state,
			promotionsList: sortedPromotionsList,
			promotionsListCopy: sortedPromotionsListCopy,
		};
	},

	[promotionsNamespacer(promotionsAT.SET_UPDATE_ERRORS)]: (state, action) => {
		return {
			...state,
			activePromotionDetailsUpdateErrors: {
				...state.activePromotionDetailsUpdateErrors,
				...action.payload,
			},
		};
	},
	[promotionsNamespacer(promotionsAT.GET_REFERRAL_PROGRAM_STARTED)]: (state) => {
		return {
			...state,
			referralProgramInfoCallInProgress: true,
			referralProgramInfoError: '',
		};
	},
	[promotionsNamespacer(promotionsAT.GET_REFERRAL_PROGRAM_SUCCEEDED)]: (
		state,
		action,
	) => {
		return {
			...state,
			referralProgramInfo: action.payload,
			referralProgramInfoCallInProgress: false,
		};
	},
	[promotionsNamespacer(promotionsAT.GET_REFERRAL_PROGRAM_FAILED)]: (state, action) => {
		return {
			...state,
			referralProgramInfoError: action.payload,
			referralProgramInfoCallInProgress: false,
		};
	},
	[promotionsNamespacer(promotionsAT.CREATE_REFERRAL_PROGRAM_STARTED)]: (state) => {
		return {
			...state,
			referralProgramInfoCallInProgress: true,
			referralProgramInfoError: '',
		};
	},
	[promotionsNamespacer(promotionsAT.CREATE_REFERRAL_PROGRAM_SUCCEEDED)]: (
		state,
		action,
	) => {
		return {
			...state,
			referralProgramInfo: action.payload,
			referralProgramInfoCallInProgress: false,
		};
	},
	[promotionsNamespacer(promotionsAT.CREATE_REFERRAL_PROGRAM_FAILED)]: (
		state,
		action,
	) => {
		return {
			...state,
			referralProgramInfoError: action.payload,
			referralProgramInfoCallInProgress: false,
		};
	},
	[promotionsNamespacer(promotionsAT.UPDATE_REFERRAL_PROGRAM_STARTED)]: (state) => {
		return {
			...state,
			referralProgramInfoCallInProgress: true,
			referralProgramInfoError: '',
		};
	},
	[promotionsNamespacer(promotionsAT.UPDATE_REFERRAL_PROGRAM_SUCCEEDED)]: (
		state,
		action,
	) => {
		return {
			...state,
			referralProgramInfo: action.payload,
			referralProgramInfoCallInProgress: false,
		};
	},
	[promotionsNamespacer(promotionsAT.UPDATE_REFERRAL_PROGRAM_FAILED)]: (
		state,
		action,
	) => {
		return {
			...state,
			referralProgramInfoError: action.payload,
			referralProgramInfoCallInProgress: false,
		};
	},
	[promotionsNamespacer(promotionsAT.GET_REFERRAL_ACTIVITY_STARTED)]: (state) => {
		return {
			...state,
			referralActivityInfoCallInProgress: true,
			referralActivityInfoError: '',
		};
	},
	[promotionsNamespacer(promotionsAT.GET_REFERRAL_ACTIVITY_SUCCEEDED)]: (
		state,
		action,
	) => {
		return {
			...state,
			referralActivityInfo: action.payload,
			referralActivityInfoCallInProgress: false,
		};
	},
	[promotionsNamespacer(promotionsAT.GET_REFERRAL_ACTIVITY_FAILED)]: (
		state,
		action,
	) => {
		return {
			...state,
			referralActivityInfoError: action.payload,
			referralActivityInfoCallInProgress: false,
		};
	},
};

export default createReducer(initialState, handlers, [nameSpace]);
